/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable consistent-return */
import React, {
  Suspense,
  useState,
  useEffect,
  useContext,
  useMemo,
} from 'react'
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom'
import './utils/Common/i18n'

import Cookies from 'universal-cookie'
import axios from 'axios'
import InscriptionPagesGuard from 'components/Routes/InscriptionPagesGuard'
import Loading from './components/Loaders/Loading'
import Screen from './layouts/Screen'
import ROUTES, { DASHBOARD_ROUTES } from './utils/ROUTES'
import AuthenticatedRoutes from './AuthenticatedRoutes'
import Footer from './components/Footers/Footer'
import AutoRefreshToken from './components/Sessions/AutoRefreshToken'
import IsEligibleToAccessNoAuthPages from './components/Routes/IsEligibleToAccessNoAuthPages'
import { AppContext } from './store/context'
import AuthenticatedScreenContainer from './layouts/AuthenticatedScreenContainer'
import DownForMaintenance from './components/Maintenances/DownForMaintenance'
import ScreenContainerWithBackground, {
  LAYOUT_CENTER,
} from './layouts/ScreenContainerWithBackground'
import ConnectionLayoutWithImageHeader from './layouts/login/ConnectionLayoutWithImageHeader'
import CenterStepsHeaderLayout from './layouts/signUp/CenterStepsHeaderLayout'
import bgLoginCreate from './assets/bg_login_create.svg'
import ScreenContainerWithBackgroundWrapper from './layouts/login/ScreenContainerWithBackgroundWrapper'
import CookiesBanner from './components/Footers/CookiesBanner'
import ErrorBoundary from './pages/ErrorBoundary'
import SuccessNotification, {
  NOTIF_MAIL_PASSWORD_SENT,
  NOTIF_SIGNUP_CREATED,
  NOTIF_SIGN_UP_LINK,
} from './components/SuccessNotification'
import ModifyUserEmail from './pages/Profile/MyProfile/ModifyUserEmail'
import EmailModified from './pages/Profile/MyProfile/EmailModified'
import EmailModifiedFail from './pages/Profile/MyProfile/EmailModifiedFail'
import PianoGTM from './utils/Common/pianoAnalytics-utils'
import {
  checkMaintenances,
  checkAccessHP,
} from './services/maintenance-service'
import MAINTENANCE_TYPE from './utils/__enums/SuperAdministration/Maintenance'
import { MaintenanceEntity } from './utils/__interfaces/SuperAdministration/Maintenance'

const ErrorPage = React.lazy(() => import('./pages/ErrorPage'))
const LoginUser = React.lazy(() => import('./pages/SignIn/LoginUser'))
const ResetPassword = React.lazy(() => import('./pages/SignIn/ResetPassword'))
const PasswordModified = React.lazy(
  () => import('./pages/SignIn/PasswordModified')
)
const NewPassword = React.lazy(() => import('./pages/SignIn/NewPassword'))
const ContactUs = React.lazy(() => import('./pages/Contact/ContactUs'))
const ContactUsOld = React.lazy(() => import('./pages/Contact/ContactUsOld'))
const Help = React.lazy(() => import('./pages/Help'))
const ContactSent = React.lazy(() => import('./pages/Contact/ContactSent'))
const UserSignUpEmailCheck = React.lazy(
  () => import('./pages/SignUp/UserSignUpEmailCheck')
)
const UserSignUpCompanyCheck = React.lazy(
  () => import('./pages/SignUp/UserSignUpCompanyCheck')
)
const UserSignUpInfos = React.lazy(
  () => import('./pages/SignUp/UserSignUpInfos')
)

const SessionExpired = React.lazy(() => import('./pages/SessionExpired'))
const Maintenance = React.lazy(() => import('./pages/Maintenances/Maintenance'))
const UploadRegister = React.lazy(
  () => import('./pages/WasteRegister/UploadRegister')
)
const PrivacyPolicy = React.lazy(() => import('./pages/Footer/PrivacyPolicy'))

const EnableMaintenance = React.lazy(
  () => import('./pages/SuperAdministration/Maintenances/EnableMaintenance')
)
declare global {
  interface Window {
    dataLayer: any[]
    QSI: any
  }
}

function App() {
  const cookies = new Cookies()
  const {
    state: { user },
  } = useContext(AppContext)
  const { roleNames } = useContext(AppContext)
  const location = useLocation()
  const history = useHistory()
  const maintenanceMode = localStorage.getItem(
    `maintenanceMode${MAINTENANCE_TYPE.ALL}`
  )

  // Manage cookies consent
  const [decision, setDecision] = useState(false)
  const [firstRender, setFirstRender] = useState(true)

  const filtredDashboardRoutes = DASHBOARD_ROUTES?.filter(
    (route) => route !== ROUTES.Administration
  )

  const [konamiAccess, setKonamiAccess] = useState(() =>
    sessionStorage.getItem('konamiAccess')
  )
  const konamiCodeEnv = process.env.REACT_APP_KONAMI_CODE ?? ''

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false)
      setTimeout(() => setDecision(true), 1500)
    }
  }, [decision, firstRender])

  const handleCookiesDecision = (choice: boolean) => {
    setDecision(choice)
  }

  const isUserConnected = useMemo(() => {
    const refreshToken = localStorage.getItem('refresh_token')
    const roles = cookies.get('current_user_roles')
    return refreshToken && roles
  }, [user.roleNames])

  // GTM Data layers - For all pages
  //---------------------------------------
  useEffect(() => {
    window.dataLayer = window.dataLayer || []
    // Data layers GTM track
    PianoGTM('Cumul de visites des pages')
  }, [location])
  //--------------------------------------

  // Check Maintenance - For all pages
  useEffect(() => {
    async function checkMaintenance() {
      const maintenances = await checkMaintenances()
      maintenances.forEach((maintenance: MaintenanceEntity) => {
        localStorage.setItem(
          `maintenanceMode${maintenance.type}`,
          maintenance.value.toString()
        )
      })
    }
    checkMaintenance()
    if (process.env.REACT_APP_ENVIRONMENT !== 'prod') checkAccessHP()
  }, [location])

  /** ****************
  Quatrics
  ******************* */
  // Loads the Site Intercept code for any defined intercepts or creatives
  useEffect(() => {
    if (window.QSI && window.QSI.API) {
      window.QSI.API.unload()
      // Loads the Site Intercept code for any defined intercepts or creatives
      window.QSI.API.load()
      window.QSI.API.run()
    }
  }, [location])

  // // Axios interceptors for maitenance check
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 503 &&
          error?.config?.url.includes(process.env.REACT_APP_BASE_API_URL)
        ) {
          localStorage.setItem(`maintenanceMode${MAINTENANCE_TYPE.ALL}`, 'true')
          sessionStorage.removeItem('konamiAccess')
          history.push(ROUTES.MaintenanceError, {
            type: MAINTENANCE_TYPE.ALL,
          })
        }
        return Promise.reject(error)
      }
    )

    // Cleanup to prevent multiple interceptors
    return () => {
      axios.interceptors.response.eject(interceptor)
    }
  }, []) // Empty dependency array ensures it runs only once

  useEffect(() => {
    // Example: setting up an interval or a custom event to check for changes in sessionStorage
    const intervalId = setInterval(() => {
      const value = sessionStorage.getItem('konamiAccess')
      if (value !== konamiAccess) {
        setKonamiAccess(value)
      }
    }, 0)

    return () => clearInterval(intervalId)
  }, [konamiAccess])

  const isFromSupportTechEnabled = Boolean(
    parseInt(process?.env?.REACT_APP_ENABLE_FORM_SUPPORT_TECHNIQUE || '0', 10)
  )

  return (
    <>
      <Router>
        <Screen>
          <Suspense fallback={<Loading />}>
            <Switch>
              {/* To manage maintenance */}
              {(Number(`${process.env.REACT_APP_MAINTENANCE}`) === 1 ||
                maintenanceMode === 'true') &&
                konamiAccess !== btoa(konamiCodeEnv) && (
                  <>
                    <DownForMaintenance />
                    <Route exact path={ROUTES.MaintenanceError}>
                      <Maintenance />
                    </Route>
                  </>
                )}

              {/* CONNECTION PAGE */}
              <Route exact path={['/', ROUTES.CheckEmail]}>
                <ErrorBoundary>
                  <IsEligibleToAccessNoAuthPages>
                    <ScreenContainerWithBackgroundWrapper
                      backgroundUrl={bgLoginCreate}
                    >
                      <ConnectionLayoutWithImageHeader />
                      <ScreenContainerWithBackground>
                        <LoginUser />
                      </ScreenContainerWithBackground>
                      {decision && !user?.isInternal ? (
                        <CookiesBanner handleDecision={handleCookiesDecision} />
                      ) : (
                        <></>
                      )}
                      <Footer />
                    </ScreenContainerWithBackgroundWrapper>
                  </IsEligibleToAccessNoAuthPages>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.Login}>
                <ErrorBoundary>
                  <IsEligibleToAccessNoAuthPages>
                    <ScreenContainerWithBackgroundWrapper
                      backgroundUrl={bgLoginCreate}
                    >
                      <ConnectionLayoutWithImageHeader />
                      <ScreenContainerWithBackground>
                        <LoginUser />
                      </ScreenContainerWithBackground>
                      <Footer />
                    </ScreenContainerWithBackgroundWrapper>
                  </IsEligibleToAccessNoAuthPages>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.UserSignUp}>
                <ErrorBoundary>
                  <IsEligibleToAccessNoAuthPages>
                    <InscriptionPagesGuard>
                      <ScreenContainerWithBackgroundWrapper
                        backgroundUrl={bgLoginCreate}
                      >
                        <CenterStepsHeaderLayout />
                        <ScreenContainerWithBackground type={LAYOUT_CENTER}>
                          <UserSignUpEmailCheck />
                        </ScreenContainerWithBackground>
                        <Footer />
                      </ScreenContainerWithBackgroundWrapper>
                    </InscriptionPagesGuard>
                  </IsEligibleToAccessNoAuthPages>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.PrivatePolicy}>
                <ErrorBoundary>
                  <PrivacyPolicy />
                  <Footer />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.UserSignUpClient}>
                <ErrorBoundary>
                  <IsEligibleToAccessNoAuthPages>
                    <InscriptionPagesGuard>
                      <ScreenContainerWithBackgroundWrapper
                        backgroundUrl={bgLoginCreate}
                      >
                        <CenterStepsHeaderLayout />
                        <ScreenContainerWithBackground type={LAYOUT_CENTER}>
                          <UserSignUpCompanyCheck />
                        </ScreenContainerWithBackground>
                        <Footer />
                      </ScreenContainerWithBackgroundWrapper>
                    </InscriptionPagesGuard>
                  </IsEligibleToAccessNoAuthPages>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.UserSignUpInfos}>
                <ErrorBoundary>
                  <IsEligibleToAccessNoAuthPages>
                    <InscriptionPagesGuard>
                      <ScreenContainerWithBackgroundWrapper
                        backgroundUrl={bgLoginCreate}
                      >
                        <CenterStepsHeaderLayout />
                        <ScreenContainerWithBackground type={LAYOUT_CENTER}>
                          <UserSignUpInfos />
                        </ScreenContainerWithBackground>
                        <Footer />
                      </ScreenContainerWithBackgroundWrapper>
                    </InscriptionPagesGuard>
                  </IsEligibleToAccessNoAuthPages>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.UserSignUpCreated}>
                <ErrorBoundary>
                  <SuccessNotification
                    type={NOTIF_SIGNUP_CREATED}
                    redirectTo={ROUTES.CheckEmail}
                  />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.MailPasswordSent}>
                <ErrorBoundary>
                  <SuccessNotification
                    type={NOTIF_MAIL_PASSWORD_SENT}
                    redirectTo={ROUTES.Login}
                  />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.NotifSignUp}>
                <ErrorBoundary>
                  <SuccessNotification
                    type={NOTIF_SIGN_UP_LINK}
                    redirectTo={ROUTES.UserSignUp}
                    showButton={false}
                    showIcon={false}
                  />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.NewPassword}>
                <ErrorBoundary>
                  <IsEligibleToAccessNoAuthPages>
                    <AutoRefreshToken />
                    <NewPassword />
                  </IsEligibleToAccessNoAuthPages>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.ContactUs}>
                <ErrorBoundary>
                  {isUserConnected && <AutoRefreshToken />}
                  {isFromSupportTechEnabled ? (
                    <ContactUs connected={isUserConnected} />
                  ) : (
                    <ContactUsOld connected={isUserConnected} />
                  )}
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.help}>
                <ErrorBoundary>
                  <IsEligibleToAccessNoAuthPages>
                    <ScreenContainerWithBackgroundWrapper
                      backgroundUrl={bgLoginCreate}
                    >
                      <ConnectionLayoutWithImageHeader />
                      <Help />
                    </ScreenContainerWithBackgroundWrapper>
                  </IsEligibleToAccessNoAuthPages>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.ContactSent}>
                <ErrorBoundary>
                  <ContactSent connected={isUserConnected} />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.CreatePassword}>
                <ErrorBoundary>
                  <AutoRefreshToken />
                  <ResetPassword mode="creation" connected={isUserConnected} />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.ResetPassword}>
                <ErrorBoundary>
                  <AutoRefreshToken />
                  <ResetPassword connected={isUserConnected} />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.ModifyUserEmail}>
                <ErrorBoundary>
                  <AutoRefreshToken />
                  <ModifyUserEmail
                    external={!user.isInternal}
                    connected={isUserConnected}
                  />
                </ErrorBoundary>
              </Route>
              <Route path={ROUTES.EmailModified}>
                <ErrorBoundary>
                  <EmailModified />
                </ErrorBoundary>
              </Route>
              <Route path={ROUTES.EmailModifiedFail}>
                <ErrorBoundary>
                  <EmailModifiedFail />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.SessionExpired}>
                <ErrorBoundary>
                  <SessionExpired />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.PasswordModified}>
                <ErrorBoundary>
                  <PasswordModified connected={isUserConnected} />
                </ErrorBoundary>
              </Route>
              <Route exact path={filtredDashboardRoutes}>
                <ErrorBoundary>
                  <AutoRefreshToken />
                  <AuthenticatedScreenContainer>
                    {roleNames.length > 0 && <AuthenticatedRoutes />}
                  </AuthenticatedScreenContainer>
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.UploadRegister}>
                <ErrorBoundary>
                  <AutoRefreshToken />
                  <UploadRegister />
                </ErrorBoundary>
              </Route>
              <Route exact path={ROUTES.confirmMaintenance}>
                <ErrorBoundary>
                  <AutoRefreshToken />
                  <EnableMaintenance />
                </ErrorBoundary>
              </Route>
              <Route>
                <ErrorBoundary>
                  <AutoRefreshToken />
                  <ErrorPage connected={isUserConnected} />
                </ErrorBoundary>
              </Route>
            </Switch>
          </Suspense>
        </Screen>
      </Router>
    </>
  )
}

export default App
